import { isIpfError } from '@ipfabric/frontend-shared-assets/utils/IpfError';
import { API_MAINTENANCE_IN_PROGRESS } from '@ipfabric/utils';
import type { GlobalStateCommandFactory } from 'components/GlobalState/GlobalStateModel';
import { setMaintenanceVisibility } from 'components/GlobalState/commands/setMaintenanceVisibility';
import { setSocketState } from 'components/GlobalState/commands/setSocketState';

type HandleInitialAppRequestCommand = () => Promise<void>;

/**
 * This is just a wrapper for requests from `initializeApp` command handling error states.
 *
 * "running maintenance" and "unavailable api" states have to be handled explicitly there,
 * because app error handler is not initialized via useErrorHandler at this component's level.
 */
export const handleInitialAppRequest: GlobalStateCommandFactory<
  HandleInitialAppRequestCommand
> = (_cmdContext, dispatch) => async (runRequest) => {
  try {
    await runRequest();
  } catch (e) {
    if (
      isIpfError(e, 'API') &&
      e.payload?.response.code === API_MAINTENANCE_IN_PROGRESS
    ) {
      dispatch(setMaintenanceVisibility, { value: true });
    } else if (isIpfError(e, 'API') && e.payload?.response.serverError) {
      // Pragmatic to indicate api is down when one of initial requests fail:
      // although this is not result of socket connection, setting this flag
      // leads to display "API unavailable" screen.
      // If one of initial requests fail on connection or unknown error,
      // it is basically the same case as if sockets weren't available.
      // Also, this will be automatically reset to 'CONNECTED' once socket is reconnected,
      // so we don't need any additional polling.
      // The case when a particular request fails but socket works is:
      // - unlikely edge case,
      // - difficult to solve automatically,
      // - easily solvable by manual user refresh.
      dispatch(setSocketState, { value: 'DISCONNECTED' });
    } else {
      throw e;
    }
  }
};
